import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';

import { RestaurantCsvPostInsights } from '@malou-io/package-dto';
import { getPlatformKeysWithRSStats, PlatformFilterPage, PlatformKey, PostType } from '@malou-io/package-utils';

import { PostInsightsV2Service } from '#core/services/post-insights-v2.service';
import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { CSV_NUMBER_LOCALE } from '#shared/helpers/create-csv-string';
import { formatShortNumber } from '#shared/helpers/format-number';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

type Data = RestaurantCsvPostInsights[];

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedPublicationsCsvInsightsServiceV2 extends AbstractCsvService<Data, Options> {
    private readonly _postInsightsServiceV2 = inject(PostInsightsV2Service);
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected override _getData$({ restaurants }: Options): Observable<Data> {
        const { endDate, startDate } = this._aggregatedStatisticsStore.datesFilter();
        const platformKeys = this._aggregatedStatisticsStore.getPlatformsFiltersByPage({
            page: PlatformFilterPage.SOCIAL_NETWORKS,
        });

        if (!startDate || !endDate) {
            return EMPTY;
        }

        const restaurantIds = restaurants.map((restaurant) => restaurant.id);
        const socialPlatformKeys = getPlatformKeysWithRSStats().filter((platformKey) => platformKeys.includes(platformKey)) as (
            PlatformKey.FACEBOOK | PlatformKey.INSTAGRAM | PlatformKey.TIKTOK
        )[];

        return this._postInsightsServiceV2.getRestaurantsCsvPostInsights$({
            restaurantIds,
            platformKeys: socialPlatformKeys,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return Array.isArray(data) && data.length > 0;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Date',
            'Location',
            'Location Internal Name',
            'Location Address',
            'Type',
            'Caption',
            'Platform',
            'Impressions',
            'Likes',
            'Comments',
            'Shares',
            'Saves',
            'Engagement Rate',
        ];
    }

    protected override _getCsvDataRows(insightsData: Data): string[][] {
        return insightsData.flatMap((restaurantCsvPostInsights) =>
            restaurantCsvPostInsights.postInsights.flatMap((postInsight) => {
                const restaurantName = restaurantCsvPostInsights.restaurantName;
                const restaurantInternalName = restaurantCsvPostInsights.restaurantInternalName ?? '';
                const restaurantAddress = restaurantCsvPostInsights.restaurantAddress ?? '';
                const postDate = new Date(postInsight.postCreatedAt).toLocaleDateString();
                const type = postInsight.postType === PostType.REEL ? 'Reel' : 'Post';
                const caption = postInsight.caption ?? '';
                const platform = this._enumTranslatePipe.transform(postInsight.platformKey, 'platform_key');
                const impressions = postInsight.impressions?.toString() ?? '';
                const likes = postInsight.likes?.toString() ?? '0';
                const comments = postInsight.comments?.toString() ?? '0';
                const shares = postInsight.shares?.toString() ?? '0';
                const saves = postInsight.saved?.toString() ?? '0';
                const engagementRate =
                    typeof postInsight.engagementRate === 'number' ? formatShortNumber(postInsight.engagementRate, CSV_NUMBER_LOCALE) : '';

                return [
                    [
                        postDate,
                        restaurantName,
                        restaurantInternalName,
                        restaurantAddress,
                        type,
                        caption,
                        platform,
                        impressions,
                        likes,
                        comments,
                        shares,
                        saves,
                        engagementRate,
                    ],
                ];
            })
        );
    }
}
